import React, { useState, useEffect, useCallback } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import TestStructureService from "../../_services/TestStructureService";

interface MonitoringRow {
  id: number;
  name: string | null;
  dateOpened: string | null;
  centreFreezeDate: string | null;
  termId: number;
  yearId: number;
  categoryId: number;
  schoolId: number | null;
  keywordId: number | null;
  programName: string;
  yearName: string;
  termName: string;
  categoryName: string;
  schoolName: string | null;
  keywordName: string | null;
}

function formatDate(d: string | null): string {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "";
  return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function formatDateForInput(d: string | null): string {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "";
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export default function TestDataEntryReportingPage() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const yearId = Number(params.get("yearId")) || 0;
  const termId = Number(params.get("termId")) || 0;
  const categoryId = Number(params.get("categoryId")) || 0;

  const [rows, setRows] = useState<MonitoringRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Checkbox selection
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // Freeze modal
  const [showFreezeModal, setShowFreezeModal] = useState(false);
  const [freezeIds, setFreezeIds] = useState<number[]>([]);
  const [freezeDate, setFreezeDate] = useState("");
  const [freezeInfo, setFreezeInfo] = useState<MonitoringRow[]>([]);
  const [freezeBusy, setFreezeBusy] = useState(false);

  // Message modal
  const [msg, setMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const loadData = useCallback(async () => {
    if (!yearId || !termId || !categoryId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await TestStructureService.getDataEntryMonitoring(yearId, termId, categoryId);
      setRows(data);
      setSelectedIds(new Set());
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [yearId, termId, categoryId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === rows.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(rows.map((r) => r.id)));
    }
  };

  // Open freeze modal for a single row
  const openFreezeSingle = (row: MonitoringRow) => {
    setFreezeIds([row.id]);
    setFreezeInfo([row]);
    // If resetting, pre-fill with existing freeze date
    if (row.centreFreezeDate) {
      setFreezeDate(formatDateForInput(row.centreFreezeDate));
    } else {
      setFreezeDate("");
    }
    setShowFreezeModal(true);
  };

  // Open freeze modal for all selected
  const openFreezeAll = () => {
    const selected = rows.filter((r) => selectedIds.has(r.id));
    if (selected.length === 0) {
      setMsg({ type: "error", text: "Please select the courses to freeze" });
      return;
    }
    setFreezeIds(selected.map((r) => r.id));
    setFreezeInfo(selected);
    setFreezeDate("");
    setShowFreezeModal(true);
  };

  const handleFreeze = async () => {
    if (!freezeDate) {
      setMsg({ type: "error", text: "Freeze Date is mandatory." });
      return;
    }
    setFreezeBusy(true);
    try {
      // Convert yyyy-MM-dd to dd/MM/yyyy for the API
      const parts = freezeDate.split("-");
      const formattedDate = `${parts[2]}/${parts[1]}/${parts[0]}`;
      const resp = await TestStructureService.freezeDataEntry(freezeIds, formattedDate);
      if (resp.success) {
        setShowFreezeModal(false);
        setMsg({ type: "success", text: resp.message || "Frozen successfully." });
        loadData();
      } else {
        setMsg({ type: "error", text: resp.message || "Failed to freeze." });
      }
    } catch (e: any) {
      setMsg({ type: "error", text: e?.response?.data?.message || "Failed to freeze." });
    } finally {
      setFreezeBusy(false);
    }
  };

  if (!yearId || !termId || !categoryId) {
    return (
      <div style={{ padding: 20 }}>
        <div style={{ padding: 20, background: "var(--status-danger-bg)", borderRadius: 8, color: "var(--status-danger)" }}>
          Missing required parameters: yearId, termId, categoryId
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 20, maxWidth: 1200, margin: "0 auto" }}>
      <div style={{ border: "1px solid var(--brand-primary)", borderRadius: 8, overflow: "hidden" }}>
        {/* Header */}
        <div style={{
          background: "var(--brand-primary)", color: "var(--text-on-brand)", padding: "12px 16px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <h4 style={{ margin: 0, fontSize: "1rem", fontWeight: 600 }}>Test Data Entry Reporting</h4>
          <button
            onClick={openFreezeAll}
            style={{
              padding: "6px 16px", borderRadius: 6, border: "none", cursor: "pointer",
              background: "var(--status-warning)", color: "var(--text-on-brand)", fontWeight: 600, fontSize: "0.85rem",
            }}
          >
            Freeze all
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 16 }}>
          {loading && (
            <div style={{ textAlign: "center", padding: 30 }}>
              <Spinner animation="border" /> Loading...
            </div>
          )}

          {!loading && error && (
            <div style={{ padding: 16, background: "var(--status-danger-bg)", borderRadius: 8, color: "var(--status-danger)" }}>
              {error}
            </div>
          )}

          {!loading && !error && rows.length === 0 && (
            <div style={{ padding: 16, background: "var(--status-danger-bg)", borderRadius: 8, color: "var(--status-danger)" }}>
              Could not find result.
            </div>
          )}

          {!loading && !error && rows.length > 0 && (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
                <thead>
                  <tr style={{ background: "var(--surface-sunken)", borderBottom: "2px solid var(--border-default)" }}>
                    <th style={{ padding: "8px 10px", textAlign: "center", width: 40 }}>
                      <input
                        type="checkbox"
                        checked={selectedIds.size === rows.length && rows.length > 0}
                        onChange={toggleSelectAll}
                      />
                    </th>
                    <th style={{ padding: "8px 10px", textAlign: "center", width: 40 }}>#</th>
                    <th style={{ padding: "8px 10px", textAlign: "left" }}>Course</th>
                    <th style={{ padding: "8px 10px", textAlign: "left" }}>Year</th>
                    <th style={{ padding: "8px 10px", textAlign: "left" }}>Semester</th>
                    <th style={{ padding: "8px 10px", textAlign: "left" }}>Category</th>
                    <th style={{ padding: "8px 10px", textAlign: "left" }}>Keyword</th>
                    <th style={{ padding: "8px 10px", textAlign: "left" }}>Center</th>
                    <th style={{ padding: "8px 10px", textAlign: "left" }}>Open date</th>
                    <th style={{ padding: "8px 10px", textAlign: "left" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr key={row.id} style={{ borderBottom: "1px solid var(--border-default)" }}>
                      <td style={{ padding: "8px 10px", textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(row.id)}
                          onChange={() => toggleSelect(row.id)}
                        />
                      </td>
                      <td style={{ padding: "8px 10px", textAlign: "center" }}>{idx + 1}</td>
                      <td style={{ padding: "8px 10px" }}>{row.programName}</td>
                      <td style={{ padding: "8px 10px" }}>{row.yearName}</td>
                      <td style={{ padding: "8px 10px" }}>{row.termName}</td>
                      <td style={{ padding: "8px 10px" }}>{row.categoryName}</td>
                      <td style={{ padding: "8px 10px" }}>{row.keywordName || "-"}</td>
                      <td style={{ padding: "8px 10px" }}>{row.schoolName || "-"}</td>
                      <td style={{ padding: "8px 10px" }}>
                        {row.dateOpened ? (
                          <span style={{
                            display: "inline-block", padding: "3px 10px", borderRadius: 4,
                            background: "var(--status-success-bg)", color: "var(--status-success-text)", fontSize: "0.82rem",
                          }}>
                            {formatDate(row.dateOpened)}
                          </span>
                        ) : (
                          <span style={{ color: "var(--text-faint)" }}>Not Open</span>
                        )}
                      </td>
                      <td style={{ padding: "8px 10px" }}>
                        {row.centreFreezeDate == null ? (
                          <button
                            onClick={() => openFreezeSingle(row)}
                            style={{
                              padding: "4px 14px", borderRadius: 4, border: "none", cursor: "pointer",
                              background: "var(--status-success)", color: "var(--text-on-brand)", fontSize: "0.82rem", fontWeight: 500,
                            }}
                          >
                            Freeze
                          </button>
                        ) : (
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{
                              display: "inline-block", padding: "3px 10px", borderRadius: 4,
                              background: "var(--status-danger-bg)", color: "var(--status-danger)", fontSize: "0.82rem",
                            }}>
                              {formatDate(row.centreFreezeDate)}
                            </span>
                            <button
                              onClick={() => openFreezeSingle(row)}
                              style={{
                                padding: "3px 10px", borderRadius: 4, border: "none", cursor: "pointer",
                                background: "var(--status-warning)", color: "var(--text-on-brand)", fontSize: "0.78rem", fontWeight: 500,
                              }}
                            >
                              Reset
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Freeze Confirmation Modal */}
      <Modal show={showFreezeModal} onHide={() => setShowFreezeModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1.1rem" }}>Confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "0.9rem", padding: "24px 28px" }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontWeight: 500, display: "block", marginBottom: 6 }}>Freeze from</label>
            <input
              type="date"
              value={freezeDate}
              onChange={(e) => setFreezeDate(e.target.value)}
              style={{
                padding: "8px 12px", border: "1px solid var(--border-strong)", borderRadius: 4,
                fontSize: "0.9rem", width: 220,
              }}
            />
          </div>
          <div style={{ marginBottom: 12, fontSize: "0.88rem" }}>
            Test Data Entry will be disabled from the <strong>Freeze From</strong> date for:
          </div>
          <div style={{ background: "var(--status-info-bg)", padding: 18, borderRadius: 8, fontSize: "0.88rem" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                {freezeInfo.length === 1 ? (
                  <>
                    <tr><td style={{ padding: "8px 12px", fontWeight: 500, width: 110 }}>Course</td><td style={{ padding: "8px 12px" }}>{freezeInfo[0].programName}</td></tr>
                    <tr><td style={{ padding: "8px 12px", fontWeight: 500, width: 110 }}>Year</td><td style={{ padding: "8px 12px" }}>{freezeInfo[0].yearName}</td></tr>
                    <tr><td style={{ padding: "8px 12px", fontWeight: 500, width: 110 }}>Semester</td><td style={{ padding: "8px 12px" }}>{freezeInfo[0].termName}</td></tr>
                    <tr><td style={{ padding: "8px 12px", fontWeight: 500, width: 110 }}>Category</td><td style={{ padding: "8px 12px" }}>{freezeInfo[0].categoryName}</td></tr>
                    <tr><td style={{ padding: "8px 12px", fontWeight: 500, width: 110 }}>Keyword</td><td style={{ padding: "8px 12px" }}>{freezeInfo[0].keywordName || "-"}</td></tr>
                    <tr><td style={{ padding: "8px 12px", fontWeight: 500, width: 110 }}>Centre</td><td style={{ padding: "8px 12px" }}>{freezeInfo[0].schoolName || "-"}</td></tr>
                  </>
                ) : (
                  <>
                    <tr>
                      <td style={{ padding: "8px 12px", fontWeight: 500, width: 110 }}>Course</td>
                      <td style={{ padding: "8px 12px" }}>{Array.from(new Set(freezeInfo.map((r) => r.programName))).join(", ")}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: "8px 12px", fontWeight: 500, width: 110 }}>Year</td>
                      <td style={{ padding: "8px 12px" }}>{Array.from(new Set(freezeInfo.map((r) => r.yearName))).join(", ")}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: "8px 12px", fontWeight: 500, width: 110 }}>Semester</td>
                      <td style={{ padding: "8px 12px" }}>{Array.from(new Set(freezeInfo.map((r) => r.termName))).join(", ")}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: "8px 12px", fontWeight: 500, width: 110 }}>Category</td>
                      <td style={{ padding: "8px 12px" }}>{Array.from(new Set(freezeInfo.map((r) => r.categoryName))).join(", ")}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: "8px 12px", fontWeight: 500, width: 110 }}>Keyword</td>
                      <td style={{ padding: "8px 12px" }}>{Array.from(new Set(freezeInfo.map((r) => r.keywordName || "-"))).join(", ")}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: "8px 12px", fontWeight: 500, width: 110 }}>Centre</td>
                      <td style={{ padding: "8px 12px" }}>{Array.from(new Set(freezeInfo.map((r) => r.schoolName || "-"))).join(", ")}</td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ padding: "16px 28px" }}>
          <Button variant="secondary" style={{ backgroundColor: "#6b7280", color: "var(--text-on-brand)", border: "none", padding: "8px 20px" }}
            onClick={() => setShowFreezeModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" style={{ backgroundColor: "var(--status-success)", color: "var(--text-on-brand)", border: "none", padding: "8px 20px" }}
            disabled={freezeBusy} onClick={handleFreeze}>
            {freezeBusy ? <Spinner animation="border" size="sm" /> : "Freeze"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Message Modal */}
      <Modal show={!!msg} onHide={() => setMsg(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>Message</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "0.85rem" }}>
          <div style={{
            padding: "10px 14px", borderRadius: 6,
            background: msg?.type === "success" ? "#dcfce7" : "#fee2e2",
            color: msg?.type === "success" ? "#166534" : "#991b1b",
          }}>
            {msg?.text}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" style={{ backgroundColor: "#6b7280", color: "var(--text-on-brand)", border: "none" }}
            onClick={() => setMsg(null)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
